import { GeneralThemesSettingsDatasyncSchemaType } from '@ps-int/general-themes/settings/schema';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AnyService, TavernInitData } from '@yandex-360/global-bar';
import { CustomTheme } from '@yandex-360/telemost-sdk';
import { WebRtcCapabilities } from '@yandex-video-platform/goloom-sdk';

import { CallType } from '../../../../../common/types/conferences';
import { MessengerBackendPreset } from '../../../../../common/types/environment';
import { RoomAgentMode } from '../../../../../common/types/room-agent';
import { QuerySettings, UserSettings } from '../../../../../common/types/settings';
import { SupportedLang, SupportedTld } from '../../../../../types/common';
import { RootState } from '../../../../init-store/index';
import { BroadcastStatus, MediaPlatform } from '../../../types';
import { ErrorPageReason, ErrorPageType } from '../../components/error-page/types';
import { BroadcastTutorialStep } from '../settings';

type Embed = 'messenger' | 'meeting-room';

export type YaServicesConfig = {
    passport: string;
    id: string;
    www: string;
    all: string;
    admin: string;
    calendar: string;
    concept: string;
    disk: string;
    fan: string;
    mail: string;
    q: string;
    notes: string;
    telemost: string;
    documents: string;
    cloudAPIUrl: string;
    yandex360: string;
    yastatic: string;
    tracker: string;
    wiki: string;
    forms: string;
    neuroExpert: string;
    'alice-pro': string;
};

export interface PSSettings {
    generalThemes: GeneralThemesSettingsDatasyncSchemaType;
    customTheme?: CustomTheme;
}

const DEFAULT_GENERAL_THEMES_SETTINGS: PSSettings['generalThemes']['settings'] = {
    isVideoEnabled: false,
    isNewYear2024ThemeEnabled: false,
};

export interface RoomAgentConfig {
    token: string;
    tokenHeaderName: string;
    mode: RoomAgentMode;
    dropHeader?: boolean;
    debug?: boolean;
    callId: string;
    h264ProfileLevelId?: string;
    syncStateWithPeerId?: string;
    recorderResolution?: string;
}

export interface TeleMessengerRedirectInfo {
    url: string;
    withAppendingHash?: boolean;
}

export type Config = {
    environment: string;
    yandexuid: string;
    yaServices: YaServicesConfig;
    errorPageType: ErrorPageType | null;
    errorPageReason?: ErrorPageReason;
    errorPageParams?: Record<string, unknown>;
    agreementShown?: boolean;
    constraintsOverrides: Array<{ models: string[]; constraints: MediaTrackConstraints }>;
    supportedLangs: SupportedLang[];
    appLang: SupportedLang;
    tld: SupportedTld;
    avatarsOrigin: string;
    userIdUrl?: string;
    calendarHost: string;
    messengerApiHost: string;
    messengerBaseUrl: string;
    currentHost: string;
    telemostHost: string;
    // TODO: будет удалено в это задачке https://st.yandex-team.ru/TELEMOSTFRONT-1997
    ruTelemostConferenceHost?: string;
    vhLiveHost: string;
    cookierHost: string;
    geoTimezone: string;
    experiments: {
        metrika: string;
        flags: Record<string, boolean | Record<string, string | number | boolean>>;
        boxes: string[];
    };
    background: number;
    desktopAppPromoClosed?: boolean;
    desktopAppPromoClosedInSession?: boolean;
    roomId?: string;
    broadcastStatus?: BroadcastStatus;
    broadcastTutorialStep?: BroadcastTutorialStep;
    clientCookiesDomain: string;
    broadcastCaptionTooltipShown?: boolean;
    userSettings?: UserSettings;
    autoJoin: boolean;
    guestName?: string;
    recorderVideoBitsPerSecond?: number;
    recorderAudioBitsPerSecond?: number;
    hideParticipantName: boolean;
    hideNonCallsRelatedInterface: boolean;
    hideLogs: boolean;
    conferenceBackground?: string;
    psSettings?: PSSettings;
    nonce: string;
    reqid: string;
    serverTime: number;
    embed?: Embed;
    messengerIntegrationTier?: number;
    isOneOnOne: boolean;
    disableLocalRecording: boolean;
    disableAddMembersButton: boolean;
    showCopyLinkButton: boolean;
    syncMediaDevices: boolean;
    devices: {
        audioInputLabel?: string;
        audioOutputLabel?: string;
        videoInputLabel?: string;
    };
    /**
     * Включает отправку записи экрана через PM и сохранение на стороне хоста
     */
    pmScreenRecording: boolean;
    screenRecordingLimit?: number;
    hideRecordingLimitPopup: boolean;
    disableSharingOverDc: boolean;
    isDesktopApp: boolean;
    isTelemostDesktopApp: boolean;
    translatorToken?: string;
    dropTranslatorTokenHeader?: boolean;
    /**
     * conferenceMediaPlatform: медиа платформа конференции,
     * к которой подключены в данный момент, или значение для предыдущей конференции,
     * если мы сейчас не подключены к какой-либо
     */
    conferenceMediaPlatform?: MediaPlatform;
    /**
     * mediaPlatform: медиа платформа, поддерживаемая текущим клиентом,
     * не зависит от медиа платформы конференции
     */
    mediaPlatform: MediaPlatform;
    roomAgent?: RoomAgentConfig;
    ipv6?: boolean;
    /**
     * Возможности WebRtc переданные Goloom-ом
     */
    webRtcCapabilities?: WebRtcCapabilities;
    /**
     * Выключает редиректы в ПО и мобильные приложения
     */
    withoutRedirect?: boolean;
    /**
     * Произошла ли перезагрузка страницы после создания конференции из-за разницы медиаплатформ
     */
    createdWithReload?: boolean;
    /**
     * Версия приложения
     */
    appVersion: string;

    /**
     * Предлагаем потенциальной ятим-учётке авторизоваться под ССО
     */
    showUserMismatchesOrganization: boolean;

    /**
     * Является ли текущий хост B2B-доменом
     */
    isB2BHost: boolean;

    /**
     * Является ли сборка релиз-кандидатом
     */
    isGoloomRc?: 1;

    /**
     * Является ли сборка OnPremise инсталляцией
     */
    isOnPremise: boolean;

    /**
     * Должны ли дождаться внешнего акцепта звонка при предзагрузке iframe
     */
    waitAccept?: boolean;

    /**
     * Встреча открыта из Messenger в оконном полноэкранном режиме.
     * Это не браузерный Fullscreen API: iframe остается внутри страницы Messenger,
     * а состояние fullscreen синхронизируется с родителем через postMessage.
     */
    meetingFullscreen?: boolean;

    /**
     * Использовать ли ПДОК для отрисовки шапки
     */
    usePdokHeader?: boolean;

    /**
     * Открыт ли клиент из YandexNet
     */
    isYandexNet?: boolean;

    /**
     * Пресет бекенда для виджета мессенджера
     */
    messengerBackendPreset?: MessengerBackendPreset;

    /**
     * Урл, на который будет происходить редирект, при продолжении пользователя в браузере
     * на странице-заглушке ТелеМессенджера
     */
    teleMessengerRedirectInfo?: TeleMessengerRedirectInfo;

    /**
     * Uid из query-параметра selectedUid
     */
    selectedUid?: string;

    /**
     * login (с почтой) из query-параметра selectedLogin
     */
    selectedLogin?: string;

    /**
     * Uid Я.Тим SSO аккаунта пользователя, который надо подставить вместо текущего
     * uid'а в виджет чата при подмене организации.
     * Считается на BFF, см. resolve-chat-substituted-uid.ts. Используется только
     * в чат-сценариях через `selectChatUserUid` / `getChatUserUid`.
     */
    chatSubstitutedUid?: string;

    /**
     * Данные из Таверны для инициализации GlobalBar
     */
    globalBarInitData?: TavernInitData;

    /**
     * Список сервисов, разрешённых в GlobalBar.
     * Используется в OnPremise, если задан через env.
     */
    globalBarOnPremServices?: AnyService[];

    /**
     * Запущены внутри теле-мессенджера
     */
    isTeleMessenger?: boolean;

    /**
     * Открыта История Встреч внутри теле-мессенджера
     */
    isTeleMessengerConferencesHistory?: boolean;
    /**
     * Настройки из query
     */
    querySettings?: QuerySettings;

    /**
     * URL JSON-конфига слот-плагинов.
     */
    slotPluginsConfigUrl?: string;
    /**
     * Тип звонка в messenger'е: conference или direct
     */
    callType?: CallType;

    /**
     * Guid текущего пользователя в Messenger.
     * Передается из Messenger в iframe Telemost для RUM-событий.
     */
    userGuid?: string;
};

export type SetErrorPageActionPayload =
    | ({ params?: Record<string, unknown> } & (
          | {
                type: ErrorPageType.DEFAULT;
                reason: ErrorPageReason;
            }
          | {
                type: Exclude<ErrorPageType, ErrorPageType.DEFAULT>;
                reason?: ErrorPageReason;
            }
      ))
    | null;

export type UpdateTeleMessengerRedirectInfo = Partial<TeleMessengerRedirectInfo>;

export const getConfigSelector = (state: RootState) => state.config;

export const configSlice = createSlice({
    name: '@CONFIG',
    initialState: {} as Config,
    reducers: {
        setErrorPage(state: Config, action: PayloadAction<SetErrorPageActionPayload>) {
            state.errorPageReason = action.payload?.reason;
            state.errorPageType = action.payload?.type ?? null;
            if (action.payload?.params) {
                state.errorPageParams = action.payload.params;
            }
        },
        resetErrorPage(state) {
            state.errorPageType = null;
            state.errorPageReason = undefined;
            state.errorPageParams = undefined;
        },
        setAgreementShown(state: Config) {
            state.agreementShown = true;
        },
        setAppLang(state: Config, action: PayloadAction<SupportedLang>) {
            state.appLang = action.payload;
        },
        setDesktopAppPromoClosed(state: Config) {
            state.desktopAppPromoClosed = true;
            state.desktopAppPromoClosedInSession = true;
        },
        setBroadcastTutorialStep(state, { payload }: PayloadAction<BroadcastTutorialStep>) {
            state.broadcastTutorialStep = payload;
        },
        setBroadcastCaptionTooltipShown(state, { payload }: PayloadAction<boolean>) {
            state.broadcastCaptionTooltipShown = payload;
        },
        setTheme(state, { payload }: PayloadAction<GeneralThemesSettingsDatasyncSchemaType>) {
            state.psSettings = {
                ...state.psSettings,
                generalThemes: {
                    ...state.psSettings?.generalThemes,
                    theme: payload.theme,
                    settings: payload.settings,
                },
            };
        },
        setCustomTheme(state, { payload }: PayloadAction<CustomTheme>) {
            const currentGeneralThemes = state.psSettings?.generalThemes;

            state.psSettings = {
                ...state.psSettings,
                generalThemes: {
                    theme: currentGeneralThemes?.theme ?? null,
                    settings: {
                        ...DEFAULT_GENERAL_THEMES_SETTINGS,
                        ...currentGeneralThemes?.settings,
                    },
                },
                customTheme: payload,
            };
        },
        setVideoBg(state, { payload }: PayloadAction<boolean>) {
            // В OnPrem сервер больше не прелоадит `psSettings`, поэтому к моменту открытия настроек
            // `state.psSettings` может быть пустым. При этом переключатель video background должен
            // продолжать работать и сразу отражаться в Redux. Поэтому здесь достраиваем минимальный
            // `generalThemes` state из дефолтов и уже выбранной темы, если она есть.
            const currentGeneralThemes = state.psSettings?.generalThemes;

            state.psSettings = {
                ...state.psSettings,
                generalThemes: {
                    theme: currentGeneralThemes?.theme ?? null,
                    settings: {
                        ...DEFAULT_GENERAL_THEMES_SETTINGS,
                        ...currentGeneralThemes?.settings,
                        isVideoEnabled: payload,
                    },
                },
            };
        },
        setConferenceMediaPlatform(state, { payload }: PayloadAction<MediaPlatform>) {
            state.conferenceMediaPlatform = payload;
        },
        setWebRtcCapabilities(state, { payload }: PayloadAction<WebRtcCapabilities>) {
            state.webRtcCapabilities = payload;
        },
        setIsOneOnOne(state, { payload }: PayloadAction<boolean>) {
            state.isOneOnOne = payload;
        },
        setAutoJoin(state, { payload }: PayloadAction<boolean>) {
            state.autoJoin = payload;
        },
        setCreatedWithReload(state, { payload }: PayloadAction<boolean>) {
            state.createdWithReload = payload;
        },
        updateTeleMessengerRedirectInfo(state, { payload }: PayloadAction<UpdateTeleMessengerRedirectInfo>) {
            if (state.teleMessengerRedirectInfo) {
                state.teleMessengerRedirectInfo = {
                    ...state.teleMessengerRedirectInfo,
                    ...payload,
                };
            }
        },
        resetConfigRoomId(state) {
            state.roomId = undefined;
        },
    },
});

export const configReducer = configSlice.reducer;

export const {
    updateTeleMessengerRedirectInfo,
    setCustomTheme,
    setErrorPage: setErrorPageAction,
    resetErrorPage,
    setBroadcastTutorialStep,
    setBroadcastCaptionTooltipShown,
    setTheme,
    setVideoBg,
    setConferenceMediaPlatform,
    setIsOneOnOne,
    setAutoJoin,
    setCreatedWithReload,
    resetConfigRoomId,
} = configSlice.actions;

export const checkHasDesktopAppAction = createAction('@CONFIG/CHECK_HAS_DESKTOP_APP');
export const setDesktopAppPromoClosed = createAction('@CONFIG/SET_DESKTOP_APP_PROMO_CLOSED');
export const actualizeTeleMessengerUrlAction = createAction('@CONFIG/ACTUALIZE_TELEMESSENGER_URL');
